import React, { useEffect, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, ArrowLeft, Save } from "lucide-react";
import api, { formatApiErrorDetail, inr } from "./api";

const empty = {
  personal: {
    full_name: "", fathers_name: "", dob: "", gender: "", marital_status: "", blood_group: "",
    mobile: "", personal_email: "", official_email: "", aadhaar: "", pan: "", passport: "",
  },
  employment: {
    employee_code: "", date_of_joining: "", department: "", designation: "", reporting_manager: "",
    employment_type: "Permanent", office_location: "", work_location: "", employee_status: "Active",
    probation_period: "", uan: "", pf_number: "", esi_number: "",
  },
  salary: {
    monthly_ctc: 0, basic: 0, hra: 0, conveyance: 0, special_allowance: 0, medical_allowance: 0,
    bonus: 0, incentive: 0, employer_pf: 0, employee_pf: 0, esi: 0, professional_tax: 0, tds: 0, other_deductions: 0,
  },
  bank: { bank_name: "", account_number: "", ifsc: "", branch: "", upi: "" },
};

const Field = ({ label, children }) => (
  <div>
    <label className="mono-label block mb-2" style={{ color: "#94A3B8" }}>{label}</label>
    {children}
  </div>
);

const Section = ({ title, children }) => (
  <div className="bg-[#0C1322] border border-white/10 p-6 mb-5">
    <h2 className="font-display font-bold text-lg text-white mb-5">{title}</h2>
    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">{children}</div>
  </div>
);

export default function EmployeeForm() {
  const { id } = useParams();
  const isEdit = !!id;
  const navigate = useNavigate();
  const [form, setForm] = useState(empty);
  const [meta, setMeta] = useState({ employment_types: [], employee_statuses: [] });
  const [departments, setDepartments] = useState([]);
  const [designations, setDesignations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    Promise.all([api.get("/hr/meta"), api.get("/hr/departments"), api.get("/hr/designations")])
      .then(([m, d, g]) => {
        setMeta(m.data);
        setDepartments(d.data.map((x) => x.name));
        setDesignations(g.data.map((x) => x.name));
      })
      .catch(() => {});
    if (isEdit) {
      setLoading(true);
      api.get(`/hr/employees/${id}`)
        .then((r) => setForm({ personal: r.data.personal, employment: r.data.employment, salary: r.data.salary, bank: r.data.bank }))
        .catch(() => toast.error("Failed to load employee"))
        .finally(() => setLoading(false));
    }
  }, [id, isEdit]);

  const set = (group, key) => (e) => {
    const v = e && e.target ? e.target.value : e;
    setForm((f) => ({ ...f, [group]: { ...f[group], [key]: v } }));
  };
  const setNum = (key) => (e) =>
    setForm((f) => ({ ...f, salary: { ...f.salary, [key]: e.target.value === "" ? 0 : parseFloat(e.target.value) } }));

  const s = form.salary;
  const gross = ["basic", "hra", "conveyance", "special_allowance", "medical_allowance", "bonus", "incentive"]
    .reduce((a, k) => a + (parseFloat(s[k]) || 0), 0);
  const ded = ["employee_pf", "esi", "professional_tax", "tds", "other_deductions"]
    .reduce((a, k) => a + (parseFloat(s[k]) || 0), 0);
  const net = gross - ded;

  const submit = async (e) => {
    e.preventDefault();
    if (!form.personal.full_name.trim()) { toast.error("Full name is required"); return; }
    setSaving(true);
    try {
      if (isEdit) {
        await api.put(`/hr/employees/${id}`, form);
        toast.success("Employee updated");
      } else {
        const { data } = await api.post("/hr/employees", form);
        toast.success(`Employee created — ${data.employee_id}`);
      }
      navigate("/portal/employees");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="p-12 flex justify-center"><Loader2 className="animate-spin" style={{ color: "#00F0FF" }} /></div>;

  const inputCls = "field";
  const selectCls = "field appearance-none cursor-pointer";

  return (
    <div data-testid="employee-form-page">
      <Link to="/portal/employees" className="inline-flex items-center gap-2 mono-label mb-4 hover:text-cyan" style={{ color: "#64748B" }}>
        <ArrowLeft size={14} /> Back to employees
      </Link>
      <h1 className="font-display font-extrabold text-3xl md:text-4xl text-white tracking-tight mb-6">
        {isEdit ? "Edit Employee" : "Add Employee"}
      </h1>

      <form onSubmit={submit} data-testid="employee-form">
        <Section title="Personal Information">
          <Field label="Full Name *"><input required className={inputCls} value={form.personal.full_name} onChange={set("personal", "full_name")} data-testid="f-full_name" /></Field>
          <Field label="Father's Name"><input className={inputCls} value={form.personal.fathers_name} onChange={set("personal", "fathers_name")} /></Field>
          <Field label="Date of Birth"><input type="date" className={inputCls} value={form.personal.dob} onChange={set("personal", "dob")} /></Field>
          <Field label="Gender">
            <select className={selectCls} value={form.personal.gender} onChange={set("personal", "gender")}>
              <option value="">Select</option><option>Male</option><option>Female</option><option>Other</option>
            </select>
          </Field>
          <Field label="Marital Status">
            <select className={selectCls} value={form.personal.marital_status} onChange={set("personal", "marital_status")}>
              <option value="">Select</option><option>Single</option><option>Married</option>
            </select>
          </Field>
          <Field label="Blood Group"><input className={inputCls} value={form.personal.blood_group} onChange={set("personal", "blood_group")} /></Field>
          <Field label="Mobile Number"><input className={inputCls} value={form.personal.mobile} onChange={set("personal", "mobile")} data-testid="f-mobile" /></Field>
          <Field label="Personal Email"><input type="email" className={inputCls} value={form.personal.personal_email} onChange={set("personal", "personal_email")} /></Field>
          <Field label="Official Email"><input type="email" className={inputCls} value={form.personal.official_email} onChange={set("personal", "official_email")} /></Field>
          <Field label="Aadhaar Number"><input className={inputCls} value={form.personal.aadhaar} onChange={set("personal", "aadhaar")} /></Field>
          <Field label="PAN Number"><input className={inputCls} value={form.personal.pan} onChange={set("personal", "pan")} /></Field>
          <Field label="Passport (Optional)"><input className={inputCls} value={form.personal.passport} onChange={set("personal", "passport")} /></Field>
        </Section>

        <Section title="Employment Details">
          <Field label="Employee Code"><input className={inputCls} value={form.employment.employee_code} onChange={set("employment", "employee_code")} /></Field>
          <Field label="Date of Joining"><input type="date" className={inputCls} value={form.employment.date_of_joining} onChange={set("employment", "date_of_joining")} data-testid="f-doj" /></Field>
          <Field label="Department">
            <select className={selectCls} value={form.employment.department} onChange={set("employment", "department")} data-testid="f-department">
              <option value="">Select</option>{departments.map((d) => <option key={d}>{d}</option>)}
            </select>
          </Field>
          <Field label="Designation">
            <select className={selectCls} value={form.employment.designation} onChange={set("employment", "designation")}>
              <option value="">Select</option>{designations.map((d) => <option key={d}>{d}</option>)}
            </select>
          </Field>
          <Field label="Reporting Manager"><input className={inputCls} value={form.employment.reporting_manager} onChange={set("employment", "reporting_manager")} /></Field>
          <Field label="Employment Type">
            <select className={selectCls} value={form.employment.employment_type} onChange={set("employment", "employment_type")}>
              {meta.employment_types.map((t) => <option key={t}>{t}</option>)}
            </select>
          </Field>
          <Field label="Office Location"><input className={inputCls} value={form.employment.office_location} onChange={set("employment", "office_location")} /></Field>
          <Field label="Work Location"><input className={inputCls} value={form.employment.work_location} onChange={set("employment", "work_location")} /></Field>
          <Field label="Employee Status">
            <select className={selectCls} value={form.employment.employee_status} onChange={set("employment", "employee_status")} data-testid="f-status">
              {meta.employee_statuses.map((t) => <option key={t}>{t}</option>)}
            </select>
          </Field>
          <Field label="Probation Period"><input className={inputCls} value={form.employment.probation_period} onChange={set("employment", "probation_period")} placeholder="e.g. 6 months" /></Field>
          <Field label="UAN"><input className={inputCls} value={form.employment.uan} onChange={set("employment", "uan")} /></Field>
          <Field label="PF Number"><input className={inputCls} value={form.employment.pf_number} onChange={set("employment", "pf_number")} /></Field>
          <Field label="ESI Number"><input className={inputCls} value={form.employment.esi_number} onChange={set("employment", "esi_number")} /></Field>
        </Section>

        <Section title="Salary Structure (Monthly ₹)">
          <Field label="Monthly CTC"><input type="number" className={inputCls} value={s.monthly_ctc} onChange={setNum("monthly_ctc")} /></Field>
          <Field label="Basic"><input type="number" className={inputCls} value={s.basic} onChange={setNum("basic")} data-testid="f-basic" /></Field>
          <Field label="HRA"><input type="number" className={inputCls} value={s.hra} onChange={setNum("hra")} /></Field>
          <Field label="Conveyance"><input type="number" className={inputCls} value={s.conveyance} onChange={setNum("conveyance")} /></Field>
          <Field label="Special Allowance"><input type="number" className={inputCls} value={s.special_allowance} onChange={setNum("special_allowance")} /></Field>
          <Field label="Medical Allowance"><input type="number" className={inputCls} value={s.medical_allowance} onChange={setNum("medical_allowance")} /></Field>
          <Field label="Bonus"><input type="number" className={inputCls} value={s.bonus} onChange={setNum("bonus")} /></Field>
          <Field label="Incentive"><input type="number" className={inputCls} value={s.incentive} onChange={setNum("incentive")} /></Field>
          <Field label="Employer PF"><input type="number" className={inputCls} value={s.employer_pf} onChange={setNum("employer_pf")} /></Field>
          <Field label="Employee PF"><input type="number" className={inputCls} value={s.employee_pf} onChange={setNum("employee_pf")} /></Field>
          <Field label="ESI"><input type="number" className={inputCls} value={s.esi} onChange={setNum("esi")} /></Field>
          <Field label="Professional Tax"><input type="number" className={inputCls} value={s.professional_tax} onChange={setNum("professional_tax")} /></Field>
          <Field label="TDS"><input type="number" className={inputCls} value={s.tds} onChange={setNum("tds")} /></Field>
          <Field label="Other Deductions"><input type="number" className={inputCls} value={s.other_deductions} onChange={setNum("other_deductions")} /></Field>
        </Section>

        <div className="bg-[#131C31] border border-cyan/30 p-5 mb-5 grid grid-cols-3 gap-4" data-testid="salary-summary">
          <div><div className="mono-label" style={{ color: "#64748B" }}>Gross</div><div className="font-display font-bold text-lg text-white mt-1">{inr(gross)}</div></div>
          <div><div className="mono-label" style={{ color: "#64748B" }}>Deductions</div><div className="font-display font-bold text-lg text-white mt-1">{inr(ded)}</div></div>
          <div><div className="mono-label" style={{ color: "#64748B" }}>Net / Month</div><div className="font-display font-bold text-lg mt-1" style={{ color: "#00F0FF" }}>{inr(net)}</div></div>
        </div>

        <Section title="Bank Details">
          <Field label="Bank Name"><input className={inputCls} value={form.bank.bank_name} onChange={set("bank", "bank_name")} /></Field>
          <Field label="Account Number"><input className={inputCls} value={form.bank.account_number} onChange={set("bank", "account_number")} /></Field>
          <Field label="IFSC Code"><input className={inputCls} value={form.bank.ifsc} onChange={set("bank", "ifsc")} /></Field>
          <Field label="Branch"><input className={inputCls} value={form.bank.branch} onChange={set("bank", "branch")} /></Field>
          <Field label="UPI ID (Optional)"><input className={inputCls} value={form.bank.upi} onChange={set("bank", "upi")} /></Field>
        </Section>

        <div className="flex gap-3">
          <button type="submit" disabled={saving} data-testid="save-employee"
            className="group inline-flex items-center gap-2 bg-[#0066FF] hover:bg-[#00F0FF] text-white hover:text-[#060A14] px-7 py-3.5 mono-label transition-colors duration-300 disabled:opacity-60">
            {saving ? <Loader2 size={16} className="animate-spin" /> : <><Save size={16} /> {isEdit ? "Update Employee" : "Save Employee"}</>}
          </button>
          <button type="button" onClick={() => navigate("/portal/employees")}
            className="inline-flex items-center gap-2 border border-white/15 text-white px-7 py-3.5 mono-label hover:border-white/40 transition-colors">
            Cancel
          </button>
        </div>
      </form>
    </div>
  );
}
